import React from 'react';
import Icon from '../../../components/AppIcon';

const AvailabilityStatus = () => {
  const currentAvailability = {
    status: 'available',
    capacity: '75%',
    nextAvailable: 'January 2025',
    preferredProjects: [
      'React/Next.js Applications',
      'Full-Stack Development',
      'Technical Consulting',
      'Code Reviews & Audits'
    ],
    workingHours: 'Mon-Fri, 9 AM - 6 PM EST',
    responseTime: 'Within 24 hours',
    timezone: 'Eastern Time (UTC-5)'
  };

  const statusConfig = {
    available: {
      color: 'text-success',
      bgColor: 'bg-success/10',
      borderColor: 'border-success/20',
      icon: 'CheckCircle',
      label: 'Available for New Projects'
    },
    limited: {
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/20',
      icon: 'Clock',
      label: 'Limited Availability'
    },
    busy: {
      color: 'text-error',
      bgColor: 'bg-error/10',
      borderColor: 'border-error/20',
      icon: 'XCircle',
      label: 'Currently Unavailable'
    }
  };

  const config = statusConfig?.[currentAvailability?.status];

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Current Availability
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Transparent information about my current capacity and preferred collaboration types.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Status Card */}
          <div className={`bg-card border ${config?.borderColor} rounded-2xl p-8 ${config?.bgColor}`}>
            <div className="flex items-center space-x-4 mb-6">
              <div className={`w-12 h-12 ${config?.bgColor} rounded-full flex items-center justify-center`}>
                <Icon name={config?.icon} size={24} className={config?.color} />
              </div>
              <div>
                <h3 className={`text-xl font-bold ${config?.color}`}>
                  {config?.label}
                </h3>
                <p className="text-text-secondary">
                  Current capacity: {currentAvailability?.capacity} booked
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-border/50">
                <span className="text-text-secondary">Next Available:</span>
                <span className="font-semibold text-text-primary">
                  {currentAvailability?.nextAvailable}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-border/50">
                <span className="text-text-secondary">Response Time:</span>
                <span className="font-semibold text-text-primary">
                  {currentAvailability?.responseTime}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-border/50">
                <span className="text-text-secondary">Working Hours:</span>
                <span className="font-semibold text-text-primary">
                  {currentAvailability?.workingHours}
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-text-secondary">Timezone:</span>
                <span className="font-semibold text-text-primary">
                  {currentAvailability?.timezone}
                </span>
              </div>
            </div>
          </div>

          {/* Preferred Projects */}
          <div className="bg-card border border-border rounded-2xl p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                <Icon name="Heart" size={24} className="text-accent" />
              </div>
              <h3 className="text-xl font-bold text-text-primary">
                Preferred Project Types
              </h3>
            </div>

            <div className="space-y-3">
              {currentAvailability?.preferredProjects?.map((project, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-text-primary font-medium">{project}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-accent/5 border border-accent/20 rounded-lg">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-accent mt-0.5" />
                <div>
                  <h4 className="font-semibold text-accent mb-1">Project Minimums</h4>
                  <p className="text-sm text-text-secondary">
                    I typically work on projects with a minimum budget of $5,000 and 
                    timeline of at least 2 weeks to ensure quality delivery.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Communication Preferences */}
        <div className="mt-12 bg-card border border-border rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-text-primary mb-6 text-center">
            Communication Preferences
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-muted/30 rounded-xl">
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="MessageCircle" size={24} className="text-accent" />
              </div>
              <h4 className="font-semibold text-text-primary mb-2">Initial Contact</h4>
              <p className="text-sm text-text-secondary">
                Email or contact form for detailed project discussions
              </p>
            </div>

            <div className="text-center p-6 bg-muted/30 rounded-xl">
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Video" size={24} className="text-success" />
              </div>
              <h4 className="font-semibold text-text-primary mb-2">Project Kickoff</h4>
              <p className="text-sm text-text-secondary">
                Video call to align on goals, timeline, and expectations
              </p>
            </div>

            <div className="text-center p-6 bg-muted/30 rounded-xl">
              <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Calendar" size={24} className="text-warning" />
              </div>
              <h4 className="font-semibold text-text-primary mb-2">Regular Updates</h4>
              <p className="text-sm text-text-secondary">
                Weekly progress reports and milestone check-ins
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AvailabilityStatus;